import { useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';

import { SalesItemReport } from '@/domain/models/SalesReport';
import { getClient } from '@/lib/restClient';
import { FilterParams, SalesReportService } from '@/services/SalesReport';
import { SalesFilter } from '@/services/SalesReport/hooks/useSalesReportFilter';
import { useSalesItemState } from '@/store/sales_aggregation';
interface Report {
  salesItem: SalesItemReport;
  isLoading: boolean;
  error: Error | null;
}

const useItemsSales = (params: FilterParams, enabled = false): Report => {
  const salesReportService = new SalesReportService(getClient());

  const {
    data: salesItem = {} as SalesItemReport,
    error,
    isLoading,
    isFetching,
    isPreviousData,
  } = useQuery<SalesItemReport, Error>(
    ['salesItem', params],
    () => salesReportService.aggregateItemSales(params),
    {
      enabled,
      keepPreviousData: true,
    }
  );

  return {
    salesItem,
    isLoading: isLoading || (isFetching && isPreviousData),
    error: error,
  };
};

interface FilteringItemSales {
  salesItem: SalesItemReport;
  isLoading: boolean;
  error: Error | null;
  filter: SalesFilter;
  setFilter: (filter: SalesFilter) => void;
}

export const useItemsSalesFilter = (): FilteringItemSales => {
  const {
    from,
    to,
    masterBrandIDs,
    masterBrandNames,
    masterStoreIDs,
    deliveryServiceIds,
    deliveryTypes,
    authStoreIds,
    shouldFetch,
    setFrom,
    setTo,
    setMasterBrandIDs,
    setMasterBrandNames,
    setMasterStoreIDs,
    setDeliveryServiceIds,
    setDeliveryTypes,
    setAuthStoreIds,
    setShouldFetch,
  } = useSalesItemState();

  const { salesItem, isLoading, error } = useItemsSales(
    {
      startDate: from,
      endDate: to,
      masterBrandIDs,
      masterBrandNames,
      masterStoreIDs,
      deliveryServiceIds,
      deliveryTypes,
      authStoreIds,
    },
    shouldFetch
  );

  useEffect(() => {
    if (shouldFetch && !isLoading) {
      setShouldFetch(false);
    }
  }, [shouldFetch, isLoading, setShouldFetch]);

  const filter: SalesFilter = {
    from,
    to,
    masterBrandIDs,
    masterBrandNames,
    masterStoreIDs,
    deliveryServiceIds,
    deliveryTypes,
    authStoreIds,
  };

  const setFilter = useCallback(
    (filter: SalesFilter, submit = false) => {
      setFrom(filter.from);
      setTo(filter.to);
      setMasterBrandIDs(filter.masterBrandIDs);
      setMasterBrandNames(filter.masterBrandNames);
      setMasterStoreIDs(filter.masterStoreIDs);
      setDeliveryServiceIds(filter.deliveryServiceIds);
      setDeliveryTypes(filter.deliveryTypes);
      setAuthStoreIds(filter.authStoreIds);

      if (submit) {
        setShouldFetch(true);
      }
    },
    [
      setFrom,
      setTo,
      setMasterBrandIDs,
      setMasterBrandNames,
      setMasterStoreIDs,
      setDeliveryServiceIds,
      setDeliveryTypes,
      setAuthStoreIds,
      setShouldFetch,
    ]
  );

  return {
    salesItem,
    isLoading,
    error,
    filter,
    setFilter,
  };
};
